import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/format";
import { RnTab } from "@/components/AnomaliDialog";

const Th = ({ children }) => <th className="p-2">{children}</th>;

const Scroll = ({ head, children, empty, cols }) => (
  <div className="max-h-[55vh] overflow-auto rounded-lg border">
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-card"><tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">{head}</tr></thead>
      <tbody>{children}{empty && <tr><td colSpan={cols} className="p-4 text-center text-muted-foreground">Tidak ada data</td></tr>}</tbody>
    </table>
  </div>
);

const LuList = ({ lu }) => (
  <div className="space-y-3">
    <p className="text-xs text-muted-foreground">{lu.total} laporan · Marking {lu.marking} · Non Marking {lu.total - lu.marking}</p>
    <Scroll head={<><Th>No Laporan</Th><Th>No Ref (Laporan Awal)</Th><Th>Tanggal</Th><Th>Jenis</Th><Th>Rayon</Th></>} empty={!lu.rows.length} cols={5}>
      {lu.rows.map((r, i) => (
        <tr key={`${r.no_laporan}-${i}`} data-testid="live-lu-row" className="border-b">
          <td className="p-2 font-num text-xs">{r.no_laporan || "-"}</td><td className="p-2 font-num text-xs">{r.no_ref || "-"}</td>
          <td className="p-2 whitespace-nowrap">{r.tanggal}</td><td className="p-2 text-xs">{r.jenis || "-"}</td><td className="p-2 text-xs">{r.rayon || "-"}</td>
        </tr>
      ))}
    </Scroll>
  </div>
);

function YoList({ rows, period }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => rows.filter((r) => !q || `${r.nama} ${r.ulp || ""}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="live-yo-search" placeholder="Cari petugas / ULP…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" />
        <span className="text-xs text-muted-foreground">{list.length} petugas · {period} · {rows.filter((r) => r.persen >= 1).length} capai 100%</span>
      </div>
      <Scroll head={<><Th>#</Th><Th>Nama</Th><Th>ULP</Th><Th>Perusahaan</Th><Th>Performa</Th><Th>Produktivitas</Th><Th>Hari Kerja</Th><Th>Total</Th><Th>% YO</Th></>} empty={!list.length} cols={9}>
        {list.map((r, i) => (
          <tr key={`${r.id_petugas}-${i}`} data-testid="live-yo-row" className="border-b">
            <td className="p-2 font-num text-xs text-muted-foreground">{i + 1}</td><td className="p-2 font-medium">{r.nama}</td>
            <td className="p-2 text-xs">{r.ulp || "-"}</td><td className="p-2 text-xs">{r.perusahaan || "-"}</td>
            {["skor_performa", "skor_produktivitas", "skor_hari_kerja", "total_skor"].map((k) => <td key={k} className="p-2 text-right font-num">{fmt(r[k], "score")}</td>)}
            <td className={`p-2 text-right font-num font-semibold ${r.persen >= 1 ? "text-emerald-600" : r.persen >= 0.9 ? "text-amber-600" : "text-rose-600"}`}>{fmt(r.persen, "pct")}</td>
          </tr>
        ))}
      </Scroll>
    </div>
  );
}

export const LiveDetailDialog = ({ unit, params, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!unit) return;
    setData(null); setError(null);
    api.get("/live/detail", { params: { ...params, unit } }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);
  return (
    <Dialog open={!!unit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="live-detail-dialog" className="max-w-5xl w-[95vw] min-w-0 grid-cols-1 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading">Rincian Bulan Berjalan — {unit}</DialogTitle>
          <DialogDescription>{data ? `RN & LU: ${data.month_label} · YO: ${data.yo_period}` : "Memuat…"}</DialogDescription>
        </DialogHeader>
        {error && <p data-testid="live-detail-error" className="text-sm text-rose-600">{error}</p>}
        {!data && !error && <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />}
        {data && (
          <Tabs defaultValue="rn" className="min-w-0">
            <TabsList className="max-w-full overflow-x-auto justify-start">
              <TabsTrigger data-testid="live-tab-rn" value="rn">Rating Negatif ({data.rn.total})</TabsTrigger>
              <TabsTrigger data-testid="live-tab-lu" value="lu">Lapor Ulang ({data.lu.total})</TabsTrigger>
              <TabsTrigger data-testid="live-tab-yo" value="yo">Petugas YO ({data.yo.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="rn"><RnTab rn={data.rn} /></TabsContent>
            <TabsContent value="lu"><LuList lu={data.lu} /></TabsContent>
            <TabsContent value="yo"><YoList rows={data.yo} period={data.yo_period} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
